import { frenchHolidays } from "./holidays";
import { addDays, dayKeyIn, weekdayOf, zonedTime } from "./time";

// =============================================================================
// Free slots of the booking calendar: the open days and hours (Paris time),
// minus the public holidays, the closed days, the past, the calls already
// requested or confirmed and the busy times of my Google Calendar. A busy
// slot is simply not offered: nothing tells why.
// =============================================================================

export type BookingRules = {
  /** 0 = Sunday … 6 = Saturday */
  weekdays: number[];
  startHour: number;
  endHour: number;
  slotMinutes: number;
  /** Days before the first bookable day (1 = from tomorrow) */
  minNoticeDays: number;
  /** Last bookable day, in days from today */
  horizonDays: number;
  /** Closed days ("2026-12-24") */
  blockedDates: string[];
};

export type TimeRange = { start: Date; end: Date };

export const DEFAULT_BOOKING_RULES: BookingRules = {
  weekdays: [1, 2, 3, 4, 5, 6],
  startHour: 9,
  endHour: 18,
  slotMinutes: 30,
  minNoticeDays: 1,
  horizonDays: 42,
  blockedDates: [],
};

const overlaps = (a: TimeRange, b: TimeRange) =>
  a.start < b.end && b.start < a.end;

/** Bookable days (Paris), from the first to the last allowed one */
export function bookableDays(rules: BookingRules, now: Date): string[] {
  const today = dayKeyIn(now);
  const blocked = new Set(rules.blockedDates);
  const holidays = new Map<number, Set<string>>();
  const isHoliday = (day: string) => {
    const year = Number(day.slice(0, 4));
    if (!holidays.has(year)) holidays.set(year, frenchHolidays(year));
    return holidays.get(year)!.has(day);
  };

  const days: string[] = [];
  for (
    let offset = rules.minNoticeDays;
    offset <= rules.horizonDays;
    offset++
  ) {
    const day = addDays(today, offset);
    if (!rules.weekdays.includes(weekdayOf(day))) continue;
    if (blocked.has(day) || isHoliday(day)) continue;
    days.push(day);
  }
  return days;
}

export function availableSlots(
  rules: BookingRules,
  { now, busy = [] }: { now: Date; busy?: TimeRange[] }
): TimeRange[] {
  const slots: TimeRange[] = [];
  for (const day of bookableDays(rules, now)) {
    for (
      let minutes = rules.startHour * 60;
      minutes + rules.slotMinutes <= rules.endHour * 60;
      minutes += rules.slotMinutes
    ) {
      const start = zonedTime(day, minutes);
      const slot = {
        start,
        end: new Date(start.getTime() + rules.slotMinutes * 60_000),
      };
      if (start <= now || busy.some((range) => overlaps(slot, range))) continue;
      slots.push(slot);
    }
  }
  return slots;
}

/** Whether a requested start time is still a free slot */
export function isFreeSlot(
  rules: BookingRules,
  start: Date,
  context: { now: Date; busy?: TimeRange[] }
): boolean {
  return availableSlots(rules, context).some(
    (slot) => slot.start.getTime() === start.getTime()
  );
}

// =============================================================================
